import { ApolloQueryResult } from '@apollo/client';
import { BackgroundProps, Center, Flex, Spinner } from '@chakra-ui/react';
import { useOrganizationsForCurrentUserQuery } from '@client/graphql/__generated__/main-operations';
import {
  CurrentUserFragment,
  CurrentUserQuery,
  OrganizationFragment,
  OrganizationsForCurrentUserQuery,
  ProductFeatures,
} from '@client/graphql/__generated__/types';
import { usePostHog } from 'posthog-js/react';
import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCheckPermission } from '~/common/hooks/useCheckPermission';
import { useCurrentUser } from '~/common/hooks/useCurrentUser';
import { useProdFeatureFlagEnabled } from '~/common/hooks/useProdFeatureFlagEnabled';
import { useMergeState } from '~/services/document/hooks/useMergeState';

export interface AppContextValues {
  isAuthenticated: boolean;
  user?: CurrentUserFragment;
  supportedFeatures: ProductFeatures[];
  appState: {
    bgColor: BackgroundProps['bgColor'];
  };
  refetchUser: () => Promise<ApolloQueryResult<CurrentUserQuery> | void>;
  updateAppState: (state: Partial<AppContextValues['appState']>) => void;
  organizations?: OrganizationFragment[];
  selectedOrganization?: OrganizationFragment;
  loadingOrganizations: boolean;
  refetchOrganizations: () => Promise<ApolloQueryResult<OrganizationsForCurrentUserQuery> | void>;
  isSelectedOrgAdmin?: boolean;
  loadingIsSelectedOrgAdmin: boolean;
  isFeatureSupported: (feature: ProductFeatures) => boolean;
}

export const AppContext = createContext<AppContextValues>({
  isAuthenticated: false,
  user: undefined,
  supportedFeatures: [],
  appState: {
    bgColor: 'bg.main',
  },
  refetchUser: async () => {},
  updateAppState: () => {},
  loadingOrganizations: true,
  refetchOrganizations: async () => {},
  isSelectedOrgAdmin: false,
  loadingIsSelectedOrgAdmin: false,
  isFeatureSupported: () => false,
});

export const AppContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [appState, setAppState] = useState({
    bgColor: 'bg.main',
  });
  const updateAppState = useMergeState(setAppState);
  const { data, refetch, loading } = useCurrentUser();
  const {
    data: organizationsData,
    loading: loadingOrganizations,
    refetch: refetchOrganizations,
  } = useOrganizationsForCurrentUserQuery({
    fetchPolicy: 'cache-first',
    skip: !data?.currentUser,
  });

  const refetchUser = useCallback(async () => refetch(), [refetch]);
  const postHog = usePostHog();
  const isDisableIndividualAgentsFlagEnabled = useProdFeatureFlagEnabled(
    'disable-individual-agents'
  );

  const currentUser = data?.currentUser;

  // get organization from currentUser settings or first organization
  const selectedOrganization = useMemo(() => {
    return (
      currentUser?.settings?.selectedOrganization ||
      organizationsData?.organizationsForCurrentUser?.find(
        (organization) =>
          organization.id === currentUser?.settings?.selectedOrganization?.id
        // TODO: BLO-1373 - remove this fallback once all Users have UserSettings associated with them.
      ) ||
      organizationsData?.organizationsForCurrentUser?.[0]
    );
  }, [currentUser, organizationsData]);

  const {
    isAuthorized: isSelectedOrgAdmin,
    loading: loadingIsSelectedOrgAdmin,
  } = useCheckPermission(
    'accessControl.isAdmin',
    {
      skip: !selectedOrganization,
    },
    {
      organizationUuid: selectedOrganization?.id,
    }
  );

  useEffect(() => {
    if (currentUser) {
      if (selectedOrganization) {
        postHog.group('organization', selectedOrganization.id, {
          name: selectedOrganization.name,
        });
      }
      postHog.identify(currentUser.id, {
        email: currentUser.email,
        name: currentUser.fullName,
        // Setting these org-related properties here allows us to create user cohorts by
        // Organization in PostHog
        organization_uuid: selectedOrganization?.id,
        organization_name: selectedOrganization?.name,
      });
    }
  }, [currentUser, postHog, selectedOrganization]);

  const supportedFeatures = useMemo(() => {
    const isAgentSignUpEnabled = currentUser?.licenses?.some(
      (license) => license.mls?.agentSignUpEnabled
    );

    // TODO: Remove this once have e2e tests for supported features
    if (window.ENVS.environment === 'e2e') {
      return Object.values(ProductFeatures);
    }

    // If individual agent sign up is disabled
    // and the user has no selected organization
    // and the user is not part of any organization
    // then all features are disabled
    if (
      isDisableIndividualAgentsFlagEnabled &&
      !isAgentSignUpEnabled &&
      !currentUser?.settings?.selectedOrganization &&
      organizationsData?.organizationsForCurrentUser?.length === 0
    ) {
      return [];
    }

    return Array.from(
      new Set(
        currentUser?.licenses?.flatMap(
          (license) => license.mls?.supportedFeatures || []
        ) || []
      )
    );
  }, [
    currentUser?.licenses,
    currentUser?.settings?.selectedOrganization,
    organizationsData?.organizationsForCurrentUser?.length,
    isDisableIndividualAgentsFlagEnabled,
  ]);

  const isFeatureSupported = useCallback(
    (feature: ProductFeatures) => {
      return supportedFeatures.includes(feature);
    },
    [supportedFeatures]
  );

  return loading && !currentUser ? (
    <Flex alignItems="center" h="100vh" justifyContent="center" w="100vw">
      <Center>
        <Spinner />
      </Center>
    </Flex>
  ) : (
    <AppContext.Provider
      value={{
        supportedFeatures,
        refetchOrganizations,
        loadingOrganizations,
        isSelectedOrgAdmin,
        loadingIsSelectedOrgAdmin,
        isAuthenticated: !!currentUser && currentUser.roles.length > 0,
        user: currentUser,
        organizations: organizationsData?.organizationsForCurrentUser,
        selectedOrganization,
        refetchUser,
        appState,
        updateAppState,
        isFeatureSupported,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  return useContext(AppContext);
};
